import { ICellRendererParams } from "ag-grid-community";
import React from "react";
import { useDispatch } from "react-redux";

import { IgButton } from "@intelligems/ig-theme";
import { OrganizationSummarySubscriptionProductDto } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { useAdjustBilling } from "@src/api/stripe/adjust-billing";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { setDialog } from "@src/store/slices/global";

interface ModeProps {
  data: OrganizationSummarySubscriptionProductDto;
}

const Pause = ({ data }: ModeProps) => {
  const dispatch = useDispatch();

  return (
    <Grid item>
      <IgButton
        variant={"outlined"}
        onClick={() => {
          dispatch(
            setDialog({
              open: true,
              dialog: "manageSubscriptions",
              props: {
                action: "pause",
                stripeSubscriptionLinkId: data.stripeSubscriptionLinkId,
              },
            })
          );
        }}
      >
        Pause Plan
      </IgButton>
    </Grid>
  );
};

const Cancel = ({ data }: ModeProps) => {
  const dispatch = useDispatch();
  const adjustBilling = useAdjustBilling();
  const theme = useTheme();

  return (
    <Grid item>
      <IgButton
        variant={"contained"}
        color={"error"}
        loading={adjustBilling.isPending}
        onClick={() => {
          dispatch(
            setDialog({
              open: true,
              dialog: "manageSubscriptions",
              props: {
                action: "cancel",
                stripeSubscriptionLinkId: data.stripeSubscriptionLinkId,
              },
            })
          );
        }}
      >
        Cancel Plan
      </IgButton>
    </Grid>
  );
};

const CancelChanges = ({ data }: ModeProps) => {
  const adjustBilling = useAdjustBilling();

  return (
    <Grid item>
      <IgButton
        variant={"outlined"}
        loading={adjustBilling.isPending}
        onClick={() => {
          adjustBilling.mutate({
            pathParams: undefined,
            queryParams: undefined,
            body: {
              stripeSubscriptionLinkId: data.stripeSubscriptionLinkId,
              action: "release",
            },
          });
        }}
      >
        Undo Pending Changes
      </IgButton>
    </Grid>
  );
};

const ActiveMode = ({ data }: ModeProps) => {
  const hasUpcoming = !!data.subscriptionSummary?.endingPrices?.length;

  return (
    <>
      {hasUpcoming ? (
        <CancelChanges data={data} />
      ) : data.status === "trialing" ? null : (
        <Pause data={data} />
      )}
      <Cancel data={data} />
    </>
  );
};

const InactiveMode = ({ data }: ModeProps) => {
  const dispatch = useDispatch();
  const adjustBilling = useAdjustBilling();

  return (
    <Grid item>
      <IgButton
        loading={adjustBilling.isPending}
        variant={"text"}
        onClick={() => {
          adjustBilling.mutate({
            pathParams: undefined,
            queryParams: undefined,
            body: {
              stripeSubscriptionLinkId: data.stripeSubscriptionLinkId,
              action: "reactivate",
            },
          });
          dispatch(
            setDialog({
              open: true,
              dialog: "manageSubscriptions",
              props: { action: "reactivate" },
            })
          );
        }}
      >
        Undo Cancellation
      </IgButton>
    </Grid>
  );
};

export const ActionsColumn = (
  params: ICellRendererParams<OrganizationSummarySubscriptionProductDto>
) => {
  const user = useLoggedInUser();

  const orgSummary = useGetOrganizationSummary();
  if (!params.data) {
    return null;
  }

  if (!params.data.stripeSubscriptionLinkId || !orgSummary.isSuccess) {
    return null;
  }

  if (!["active", "trialing"].includes(params.data.status)) {
    return null;
  }

  if (orgSummary.data.org.agencies.length) {
    if (!params.data.subscriptionSummary?.agencyEmails?.length) {
      return null;
    }
    if (
      params.data.subscriptionSummary.agencyEmails.every(
        (email) => email !== user.data?.user?.email
      )
    ) {
      return null;
    }
  }

  return (
    <Grid container direction={"row"} columnSpacing={1} flexWrap={"nowrap"}>
      {params.data.cancelAt ? (
        <InactiveMode data={params.data} />
      ) : (
        <ActiveMode data={params.data} />
      )}
    </Grid>
  );
};
